/**
 * Sends email when chat uses @presenter or @lectura (Cloud Function + Resend).
 */

export interface ChatMentionNotifyPayload {
  kind: 'presenter' | 'lectura';
  presentationId: string;
  slideIndex: number;
  slideUrl: string;
  messageText: string;
  presentationTitle?: string;
  submitterDisplayName?: string;
  submitterEmail?: string;
}

export interface ChatMentionNotifyResult {
  /** True when the Cloud Function sent email (false for local stub / no Firebase). */
  sent: boolean;
}

export interface ChatMentionNotifyService {
  notify(payload: ChatMentionNotifyPayload): Promise<ChatMentionNotifyResult>;
}

function getFunctionsCompat(region = 'us-central1'): {
  httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }>;
} {
  const fb = typeof window !== 'undefined' ? (window as { firebase?: unknown }).firebase : null;
  if (!fb || typeof (fb as { functions?: unknown }).functions !== 'function') {
    throw new Error('Firebase Functions is not available');
  }
  const fns = (fb as {
    functions: (() => { httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }> }) & {
      region?: (region: string) => { httpsCallable(name: string): (payload?: unknown) => Promise<{ data?: unknown }> };
    };
  }).functions;
  return typeof fns.region === 'function' ? fns.region(region) : fns();
}

function toErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Notification failed';
}

export function createChatMentionNotifyStub(): ChatMentionNotifyService {
  return {
    async notify() {
      return { sent: false };
    }
  };
}

export function createFirestoreChatMentionNotifyService(opts: { region?: string } = {}): ChatMentionNotifyService {
  const region = String(opts.region || 'us-central1');
  return {
    async notify(payload: ChatMentionNotifyPayload): Promise<ChatMentionNotifyResult> {
      const svc = getFunctionsCompat(region);
      const call = svc.httpsCallable('notifyChatMention');
      try {
        const res = await call({
          kind: payload.kind,
          presentationId: payload.presentationId,
          slideIndex: payload.slideIndex,
          slideUrl: payload.slideUrl,
          messageText: payload.messageText,
          presentationTitle: payload.presentationTitle || '',
          submitterDisplayName: payload.submitterDisplayName || '',
          submitterEmail: payload.submitterEmail || ''
        });
        const data = res && (res as { data?: { ok?: boolean } }).data;
        if (!data || data.ok !== true) {
          throw new Error('Unexpected response');
        }
        return { sent: true };
      } catch (err: unknown) {
        const code =
          err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
        const details =
          err && typeof err === 'object' && 'details' in err ? (err as { details: unknown }).details : undefined;
        const detailStr = details != null && String(details).trim() ? String(details).trim() : '';
        const msg = toErrorMessage(err);
        if (code === 'functions/unauthenticated') {
          throw new Error('Sign in to notify presenters or support.');
        }
        if (code === 'functions/permission-denied') {
          throw new Error(detailStr || 'Sign in to notify presenters or support.');
        }
        if (code === 'functions/failed-precondition') {
          throw new Error(detailStr || msg || 'This action is not available.');
        }
        if (code === 'functions/resource-exhausted') {
          throw new Error(detailStr || msg || 'Too many notifications. Try again later.');
        }
        if (code === 'functions/internal') {
          throw new Error(detailStr || msg || 'Could not send email.');
        }
        throw new Error(detailStr || msg);
      }
    }
  };
}

